import React, { Suspense, useCallback, useContext, useMemo, useState } from 'react';
import { getHomePageConfig } from 'src/modules/home/HomePageMapping.ts';
import { useFilterLogic } from 'src/modules/common/hooks/useFilterLogic.ts';
import { ConfigContext } from 'src/configContext.ts';
import { composeFilter } from 'src/utils/composeFilter.ts';
import {
    UserInputKindQuery,
    UserInputKindQueryVariables,
} from 'src/generated/operations.ts';
import { MESSAGES } from 'src/modules/common/constants.ts';
import { I18n } from 'src/modules/common/components/I18n/I18n.tsx';
import { CalcVolumetricFlow } from 'src/modules/user-input/components/CalcVolumetricFlow.tsx';
import { PATHS } from 'src/router.tsx';
import { getAllowedUnits } from 'src/modules/user-input/calcV100/unitsMapping.ts';
import { VolumetricFlowCalculatorInput } from 'src/modules/user-input/calcVolumetricFlow/types.ts';
import { useUserInputState } from 'src/modules/user-input/useUserInputState.ts';
import { useEmptyResult } from 'src/modules/user-input/useEmptyResult.ts';
import { Button } from 'src/modules/common/components/Button/Button.tsx';
import { LoadingIcon } from 'src/modules/common/icons/LoadingIcon.tsx';
import { NavigationContainer } from 'src/modules/common/components/Navigation/NavigationContainer.tsx';
import { NavigationButton } from 'src/modules/common/components/Navigation/NavigationButton.tsx';
import { MainLayout } from 'src/modules/common/layouts/MainLayout.tsx';
import { BreadcrumbContainer } from 'src/common/Breadcrumb/BreadcrumbContainer.tsx';
import { useCheckMainFilter } from 'src/modules/common/hooks/useCheckMainFilter.ts';
import { useNavigate } from 'react-router-dom';
import { RuntimeContext } from 'src/runtimeContext';
import { InlineNotification } from 'src/modules/common/components/InlineNotification/InlineNotification.tsx';
import { Query } from 'src/modules/common/components/Query/Query.tsx';
import { SkeletonMainLayout } from 'src/modules/common/skeletons/SkeletonMainLayout/SkeletonMainLayout.tsx';
import { NoData } from 'src/modules/common/components/NoData/NoData.tsx';
import { getValvesPageConfig } from 'src/modules/valves/ValvesPageMapping.ts';
import { Breadcrumbs } from 'src/modules/common/types/Breadcrumbs.ts';
import { MainLayoutHeader } from 'src/modules/common/components/MainLayoutHeader/MainLayoutHeader.tsx';
import { initLocationState } from 'src/modules/locationStateContext.ts';
import { useGetHomeBreadcrumb } from 'src/modules/home/useGetHomeBreadcrumb.ts';
import { ValvesBreadcrumbValue } from '../common/components/ValvesBreadcrumbValue/ValvesBreadcrumbValue';

export const UserInputPage = (): React.ReactNode => {
    const { userSettings } = useContext(ConfigContext);
    const { isMobile } = useContext(RuntimeContext);

    const emptyResult = useEmptyResult();

    const [error, setError] = useState('');

    const pageConfig = useMemo(() => getHomePageConfig(userSettings.currentRegion), [userSettings]);
    const valvesPageConfig = useMemo(() => getValvesPageConfig(userSettings.currentRegion), [userSettings]);
    if (pageConfig === null || valvesPageConfig === null) {
        throw new Error('No Page Config!');
    }

    const {
        state: inputState, searchParams,
        setState: setInputState,
        initialState,
        locationState,
    } = useUserInputState(valvesPageConfig.filterConfig);

    const [, filter] = useFilterLogic(pageConfig.filterConfig);

    const navigate = useNavigate();

    const homePageValue = useGetHomeBreadcrumb({ filter });

    useCheckMainFilter({
        filter,
        navigate,
        searchParams,
        filterConfig: [],
    });

    const sequence1Handler = useCallback((userInput: VolumetricFlowCalculatorInput) => {
        const newInput = {
            ...inputState,
            kvs: userInput.kvs,
            kvs_u: userInput.kvs_u,
            dpmv: userInput.dpmv,
            dpmv_u: userInput.dpmv_u,
            maxDp: userInput.maxDp,
            maxDp_u: userInput.maxDp_u,
            v100: userInput.v100,
            v100_u: userInput.v100_u,
            inlet: userInput.inlet,
            inlet_u: userInput.inlet_u,
            outlet: userInput.outlet,
            outlet_u: userInput.outlet_u,
            mass: userInput.mass,
            mass_u: userInput.mass_u,
        };
        setInputState(newInput);
    }, [inputState, setInputState]);

    const sequence2Handler = useCallback((userInput: VolumetricFlowCalculatorInput) => {
        const newInput = {
            ...inputState,
            kvs_2: userInput.kvs,
            kvs_2_u: userInput.kvs_u,
            maxDp_2: userInput.maxDp,
            maxDp_2_u: userInput.maxDp_u,
            v100_2: userInput.v100,
            v100_2_u: userInput.v100_u,
        };
        setInputState(newInput);
    }, [inputState, setInputState]);

    const handleReset = useCallback(() => {
        setInputState({ ...initialState });
    }, [setInputState, initialState]);

    const allowedUnits = getAllowedUnits(userSettings.currentRegion);

    const navigation = (
        <NavigationContainer isMobile={isMobile}>
            <NavigationButton
                type="back"
                state={locationState}
                to={{
                    pathname: PATHS.Home,
                    search: searchParams.toString(),
                }}
            >
                <I18n messageId={MESSAGES.back}/>
            </NavigationButton>
            <NavigationButton
                type="forward"
                disabled={emptyResult}
                state={locationState}
                to={{
                    pathname: `${PATHS.Valves}/1`,
                    search: searchParams.toString(),
                }}
                eventData={JSON.stringify(inputState) === JSON.stringify(initialState) ? undefined : {
                    event: 'gaEvent',
                    eventCategory: 'sizing tool',
                    eventAction: 'step 2 - valve parameters',
                    eventLabel: undefined,
                }}
            >
                <I18n messageId={MESSAGES.next}/>
            </NavigationButton>
        </NavigationContainer>
    );

    const breadcrumbProps: Breadcrumbs = [
        {
            title: MESSAGES.selectValveType,
            value: homePageValue,
            state: { ...initLocationState },
            to: {
                pathname: PATHS.Home,
                search: searchParams.toString(),
            },
        },
        {
            selected: true,
            disabled: true,
            onActionClick: handleReset,
            title: MESSAGES.RequestedFlow,
            value: <ValvesBreadcrumbValue region={userSettings.currentRegion} state={inputState}/>,
            to: '#',
        },
        {
            title: MESSAGES.SelectedValve,
            disabled: true,
            value: locationState.valveLabel,
            to: '#',
        },
        {
            title: MESSAGES.SelectedActuator,
            disabled: true,
            value: locationState.actuatorLabel,
            to: '#',
        },
        {
            title: MESSAGES.SelectedAccessories,
            disabled: true,
            value: locationState.selectedAccessories.length > 0
                ? locationState.selectedAccessories.join(', ') : '-',
            to: '#',
        },
    ];

    const variables = useMemo(() => ({
        variables: {
            filter: composeFilter(pageConfig.filterConfig, filter, userSettings.currentLocale),
            region: userSettings.currentRegion,
        },
    }), [filter, pageConfig.filterConfig, userSettings.currentLocale, userSettings.currentRegion]);

    return (
        <Suspense fallback={<SkeletonMainLayout/>}>
            <MainLayout>
                <MainLayoutHeader
                    titleId={MESSAGES.userInputPageTitle}
                    navigation={isMobile ? null : navigation}
                    breadcrumbs={<BreadcrumbContainer isMobile={isMobile} breadcrumbs={breadcrumbProps}/>}
                />
                <Query<UserInputKindQuery, UserInputKindQueryVariables>
                    query={pageConfig.kindQuery}
                    variables={variables}
                >
                    {({ data }) => {
                        if (data === undefined) {
                            return <SkeletonMainLayout/>;
                        }

                        if (!data) {
                            return <NoData/>;
                        }

                        const { isNotOCCO, is6WayRequired, kind } = data.UserInputKind;

                        return (
                            <>
                                {emptyResult && (
                                    <InlineNotification
                                        type="error"
                                        message={<I18n messageId={MESSAGES.WE_RE_SORRY}/>}
                                    />
                                )}

                                {error && (
                                    <InlineNotification type="error" message={<I18n messageId={error}/>}/>
                                )}

                                <div className="vss-user-input-page">
                                    <CalcVolumetricFlow
                                        separatorTitle={is6WayRequired
                                            ? <I18n messageId={MESSAGES.userInputSequence1}/>
                                            : null}
                                        setError={setError}
                                        key={1}
                                        allowedUnits={allowedUnits}
                                        currentLocale={userSettings.currentLocale}
                                        currentRegion={userSettings.currentRegion}
                                        isModulating={isNotOCCO}
                                        kind={kind}
                                        initialState={initialState}
                                        input={{
                                            v100: inputState.v100,
                                            v100_u: inputState.v100_u,
                                            dpmv: inputState.dpmv,
                                            dpmv_u: inputState.dpmv_u,
                                            maxDp: inputState.maxDp,
                                            maxDp_u: inputState.maxDp_u,
                                            mass: inputState.mass,
                                            mass_u: inputState.mass_u,
                                            inlet: inputState.inlet,
                                            inlet_u: inputState.inlet_u,
                                            outlet: inputState.outlet,
                                            outlet_u: inputState.outlet_u,
                                            kvs: inputState.kvs,
                                            kvs_u: inputState.kvs_u,
                                        }}
                                        onChanged={sequence1Handler}
                                    />

                                    {is6WayRequired && (
                                        <CalcVolumetricFlow
                                            separatorTitle={<I18n messageId={MESSAGES.userInputSequence2}/>}
                                            setError={setError}
                                            key={2}
                                            allowedUnits={allowedUnits}
                                            currentLocale={userSettings.currentLocale}
                                            currentRegion={userSettings.currentRegion}
                                            isModulating={isNotOCCO}
                                            kind={kind}
                                            initialState={initialState}
                                            input={{
                                                ...initialState,
                                                v100: inputState.v100_2,
                                                v100_u: inputState.v100_2_u,
                                                maxDp: inputState.maxDp_2,
                                                maxDp_u: inputState.maxDp_2_u,
                                                kvs: inputState.kvs_2,
                                                kvs_u: inputState.kvs_2_u,
                                                mass: inputState.mass,
                                                mass_u: inputState.mass_u,
                                                inlet: inputState.inlet,
                                                inlet_u: inputState.inlet_u,
                                                outlet: inputState.outlet,
                                                outlet_u: inputState.outlet_u,
                                            }}
                                            onChanged={sequence2Handler}
                                        />
                                    )}

                                    <Button kind="tertiary" onClick={handleReset} fullWidth={isMobile}>
                                        <I18n messageId={MESSAGES.Reset}/>
                                        <LoadingIcon/>
                                    </Button>
                                </div>
                            </>
                        );
                    }}
                </Query>
            </MainLayout>
            {isMobile && navigation}
        </Suspense>
    );
};
